import {sleep} from '@benchling/js-utils';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {LEGACY_WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import * as EventConstants from 'constants/EventConstants';
import {Batch, Container, StorableSummaryExtras} from 'constants/SampleConstants';
import {STATUS} from 'constants/StatusConstants';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import {BioentitySummary, SchemaTag} from 'folder-items/FileConstants';
import AppDispatcher from 'lib/Dispatcher';
import {StructuredData} from 'notebook/data-tables/DataTableConstants';
import {FromServerId, ServerEntryVersion} from 'notebook/ELNConstants';
import {TransferOperation} from 'notebook/transfer-tables/TransferTableConstants';
import * as TransferTableUtil from 'notebook/transfer-tables/TransferTableUtil';
import notifications from 'notifications';
import * as ModelActions from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';
import StorableStore from 'samples/StorableStore';
import {ComputedState} from 'schemas/tags/computed/ComputedTagConstants';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import * as logging from 'util/Logging';
import {withLoadingScreen} from 'util/Promises';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import * as StageEntryActions from 'workflows/stage-entries/StageEntryActions';
import * as WorkflowActions from 'workflows/WorkflowActions';

import * as StageRunDAO from './dao/StageRunDAO';
import {CreateSpecifier, EditSpecifier} from './modals/ConfigureStageRunsModal';
import {
  RegistrationRecordList,
  StageConfiguration,
  StageEntryDetails,
  StageEntryDocument,
  StageFlowNode,
  StageRun,
  StageRunSample,
  StageRunSampleKey,
  WorkflowDetails,
  WorkflowStage,
} from './WorkflowConstants';

enum StageRunActionType {
  WORKFLOW_STAGE_RUN_SAMPLES_FETCH_STARTED = 'WORKFLOW_STAGE_RUN_SAMPLES_FETCH_STARTED',
  WORKFLOW_GRAPH_CHANGED = 'WORKFLOW_GRAPH_CHANGED',
  WORKFLOW_STAGE_ENTRIES_FETCH_COMPLETED = 'WORKFLOW_STAGE_ENTRIES_FETCH_COMPLETED',
  WORKFLOW_STAGE_RUNS_OUTPUT_SAMPLES_FETCHED = 'WORKFLOW_STAGE_RUNS_OUTPUT_SAMPLES_FETCHED',
}

export function dispatchStageRunSamplesFetched({
  stageRunSamples,
  batchesById,
  containersById,
  fileSummariesById,
  storableSummaryExtras,
}: {
  stageRunSamples?: Array<StageRunSample>;
  batchesById?: {[id: string]: Batch};
  containersById?: {[id: string]: Container};
  fileSummariesById?: {[id: string]: BioentitySummary};
  storableSummaryExtras?: StorableSummaryExtras;
} = {}): void {
  if (stageRunSamples) {
    WorkflowActions.stageRunSamplesFetched(stageRunSamples);
  }
  if (batchesById) {
    AppDispatcher.dispatch({
      actionType: EventConstants.BATCHES_BULK_FETCH_COMPLETED,
      batchesById,
    });
  }
  if (containersById) {
    AppDispatcher.dispatch({
      actionType: EventConstants.CONTAINER_BULK_FETCH_COMPLETED,
      containersById,
    });
  }
  if (fileSummariesById) {
    getStore().dispatch(ModelActions.filesFetchCompleted(fileSummariesById));
  }
  if (storableSummaryExtras) {
    AppDispatcher.dispatch({
      actionType: EventConstants.STORABLE_SUMMARIES_FETCH_COMPLETED,
      storables: [],
      storableSummaryExtras,
    });
  }
}

/**
 * Repeatedly poll for all computed tags on files with `fileIds` to gradually update the UI.
 *
 * Polls every 5 seconds until all tags are up-to-date, or for up to 60 seconds.
 *
 * Returns fileSummariesById with the computed field values, or null if computed fields failed to update.
 */
export async function updateComputedTags(
  fileIds: Array<string>
): Promise<{[id: string]: BioentitySummary} | null> {
  if (!fileIds.length) {
    return {};
  }
  for (let i = 0; ; i++) {
    const response = await FileBrowserDAO.bulkFetchFileSummaries(fileIds);
    const fileSummariesById = response?.fileSummariesById;
    dispatchStageRunSamplesFetched({fileSummariesById});
    if (
      Object.values(fileSummariesById!).every(({tags}: BioentitySummary & {tags: Array<SchemaTag>}) =>
        tags.every(
          ({computedState}: {computedState?: ComputedState | null | undefined}) =>
            !computedState || computedState.status !== STATUS.PENDING
        )
      )
    ) {
      return fileSummariesById || null;
    } else if (i >= 12) {
      logging.info('Computed tags in registration table took more than 1 minute to update', {
        extra: {fileIds},
      });
      return null;
    } else {
      await sleep(5000);
    }
  }
}

interface StageRunSamplesFetchStartedAction {
  type: StageRunActionType.WORKFLOW_STAGE_RUN_SAMPLES_FETCH_STARTED;
  stageRunSampleIds: Array<string>;
}

export async function fetchStageRunSamples(
  stageRunSampleIds: Array<string>,
  {
    includeBatches,
    includeStorableSummaryExtras,
  }: {includeBatches?: boolean; includeStorableSummaryExtras?: boolean}
): Promise<void> {
  let stageRunSampleIdsToFetch = stageRunSampleIds;
  if (!includeBatches) {
    const workflowSelector = new WorkflowSelector(getStore().getState().models.workflows);
    stageRunSampleIdsToFetch = stageRunSampleIds.filter(
      (stageRunSampleId) => !workflowSelector.isStageRunSampleIdLoading(stageRunSampleId)
    );
  }
  if (!stageRunSampleIdsToFetch.length) {
    return;
  }
  getStore().dispatch({
    type: StageRunActionType.WORKFLOW_STAGE_RUN_SAMPLES_FETCH_STARTED,
    stageRunSampleIds: stageRunSampleIdsToFetch,
  });
  await StageRunDAO.fetchStageRunSamples(stageRunSampleIdsToFetch, {
    includeBatches,
    includeStorableSummaryExtras,
  })
    .done(
      ({
        stageRunSamples,
        batchesById,
        containersById,
        fileSummariesById,
        storableSummaryExtras,
      }: StageRunDAO.FetchStageRunSamplesResponse) => {
        dispatchStageRunSamplesFetched({
          stageRunSamples,
          batchesById,
          containersById,
          fileSummariesById,
          storableSummaryExtras,
        });
        updateComputedTags(Object.keys(fileSummariesById));
      }
    )
    .fail(notifications.notifyError);
}

// Fetch the input and the output samples of the specified stage runs.
export function fetchMissingStageRunSamples(stageRuns: Array<StageRun>): void {
  let stageRunSampleIds: Array<string> = [];
  for (const stageRun of stageRuns) {
    stageRunSampleIds = [...stageRunSampleIds, ...stageRun.inputSampleIds, ...stageRun.outputSampleIds];
  }
  const workflowSelector = new WorkflowSelector(getStore().getState().models.workflows);
  // eslint-disable-next-line no-restricted-syntax
  stageRunSampleIds = _.filter(
    stageRunSampleIds,
    (sampleId) => workflowSelector.getStageRunSample(sampleId) == null
  );
  fetchStageRunSamples(stageRunSampleIds, {includeBatches: false, includeStorableSummaryExtras: true});
}

export function dispatchStageRunsFetchedAndFetchMissingSamples({
  stageRuns,
  entryIdToStructuredData,
}: {
  stageRuns: Array<StageRun>;
  entryIdToStructuredData?: {[stageEntryId: string]: StructuredData};
}): void {
  WorkflowActions.stageRunsFetched(stageRuns, entryIdToStructuredData);
  fetchMissingStageRunSamples(stageRuns);
}

export function fetchMissingRegisteredSamples(registrationRecordLists: Array<RegistrationRecordList>): void {
  let stageRunSampleIds: Array<string> = [];
  for (const registrationRecordList of registrationRecordLists) {
    stageRunSampleIds = [
      ...stageRunSampleIds,
      ...registrationRecordList.registrationRecords.map((record) => record.stageRunSampleId),
    ];
  }
  const workflowSelector = new WorkflowSelector(getStore().getState().models.workflows);

  // eslint-disable-next-line no-restricted-syntax
  stageRunSampleIds = _.filter(stageRunSampleIds, (sampleId) => {
    // Only skip if we've loaded the stage run sample, the batch, and the file.
    const stageRunSample = workflowSelector.getStageRunSample(sampleId);
    if (stageRunSample == null) {
      return true;
    }
    if (stageRunSample.fileId) {
      const file = getStore().getState().models.fileSummariesById[stageRunSample.fileId];
      if (!file) {
        return true;
      }
    } else if (stageRunSample.batch) {
      const batch = StorableStore.getBatch(stageRunSample.batch.id);
      if (!batch) {
        return true;
      }
      const file = getStore().getState().models.fileSummariesById[batch.file.id];
      if (!file) {
        return true;
      }
    }
    // Nothing further to load, since either everything was found or we don't have permissions.
    return false;
  });
  fetchStageRunSamples(stageRunSampleIds, {includeBatches: true, includeStorableSummaryExtras: false});
}

export function fetchMissingTransferredSamples(transferOperations: Array<TransferOperation>): void {
  let stageRunSampleIds = TransferTableUtil.getOutputSampleIdsForTable(transferOperations);
  const workflowSelector = new WorkflowSelector(getStore().getState().models.workflows);

  stageRunSampleIds = stageRunSampleIds.filter((sampleId) => {
    const stageRunSample = workflowSelector.getStageRunSample(sampleId);
    if (stageRunSample == null) {
      return true;
    }
    if (
      stageRunSample.containerId &&
      !StorableStore.getStorableSummaryExtras().ancestralStorablesById![stageRunSample.containerId]
    ) {
      return true;
    }
    return false;
  });
  fetchStageRunSamples(stageRunSampleIds, {includeBatches: false, includeStorableSummaryExtras: true});
}

export function deleteStageRun(
  stageRun: StageRun,
  {shouldDeleteDownstreamRuns}: {shouldDeleteDownstreamRuns: boolean}
): void {
  LoadingScreen.show();
  const stageRunEntryId = stageRun.stageEntry.id;
  StageRunDAO.deleteStageRun(stageRun.id, {shouldDeleteDownstreamRuns})
    .done((workflowDetails: WorkflowDetails) => {
      getStore().dispatch({
        type: WorkflowActions.WorkflowActionType.WORKFLOW_WORKFLOW_DETAILS_FETCH_COMPLETED,
        ...workflowDetails,
      });
      StageEntryActions.refresh([stageRunEntryId]);
    })
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

interface WorkflowGraphChangedAction {
  type: StageRunActionType.WORKFLOW_GRAPH_CHANGED;
  affectedStageFlowNodes: Array<StageFlowNode>;
  affectedStageEntries: Array<StageEntryDocument>;
  affectedStageConfigurations: Array<StageConfiguration>;
  affectedStageRuns: Array<StageRun>;
  entryIdToStructuredData?: {[entryId: string]: StructuredData};
}

export async function splitByInputs(workflowStage: WorkflowStage): Promise<void> {
  let numChangedRuns = 0;
  const workflowSelector = new WorkflowSelector(getStore().getState().models.workflows);
  for (const stageEntrySummary of workflowStage.stageEntries) {
    const stageRunsForEntry = workflowSelector.getStageRunsForEntry(stageEntrySummary.id);
    numChangedRuns += stageRunsForEntry?.length ?? 0;
  }
  analytics.deprecatedTrackEvent(LEGACY_WORKFLOWS_ANALYTICS_ACTIONS.SPLIT_BY_INPUTS, {numChangedRuns});
  const {affectedStageRuns, affectedStageFlowNodes, affectedStageEntries, affectedStageConfigurations} =
    await withLoadingScreen(StageRunDAO.splitByInputs(workflowStage), 'Saving...');
  fetchMissingStageRunSamples(affectedStageRuns);
  getStore().dispatch({
    type: StageRunActionType.WORKFLOW_GRAPH_CHANGED,
    affectedStageFlowNodes,
    affectedStageEntries,
    affectedStageConfigurations,
    affectedStageRuns,
  });
  StageEntryActions.refresh(affectedStageEntries.map((stageEntry) => stageEntry.id));
}

interface StageEntriesFetchCompletedAction {
  type: StageRunActionType.WORKFLOW_STAGE_ENTRIES_FETCH_COMPLETED;
  stageEntries: Array<StageEntryDocument>;
}

export async function bulkUpdateStageRunStatus({
  updateStatusSpecifiers,
}: {
  updateStatusSpecifiers: Array<StageRunDAO.UpdateStatusSpecifier>;
}): Promise<void> {
  LoadingScreen.show();
  try {
    const {affectedStageEntries, affectedStageRuns} = await StageRunDAO.bulkUpdateStatusStageRuns({
      updateStatusSpecifiers,
    });
    fetchMissingStageRunSamples(affectedStageRuns);
    getStore().dispatch({
      type: WorkflowActions.WorkflowActionType.WORKFLOW_STAGE_RUNS_FETCH_COMPLETED,
      stageRuns: affectedStageRuns,
    });
    getStore().dispatch({
      type: StageRunActionType.WORKFLOW_STAGE_ENTRIES_FETCH_COMPLETED,
      stageEntries: affectedStageEntries,
    });
    StageEntryActions.refresh(affectedStageEntries.map((stageEntry) => stageEntry.id));
  } finally {
    LoadingScreen.hide();
  }
}

export async function bulkConfigureStageRuns({
  createSpecifiers,
  editSpecifiers,
  workflowId,
}: {
  createSpecifiers?: Array<CreateSpecifier>;
  editSpecifiers?: Array<EditSpecifier>;
  workflowId?: string;
}): Promise<void> {
  LoadingScreen.show();
  try {
    const {workflowDetails, affectedStageEntryIds} = await StageRunDAO.bulkConfigureStageRuns({
      createSpecifiers,
      editSpecifiers,
      workflowId,
    });
    getStore().dispatch({
      type: WorkflowActions.WorkflowActionType.WORKFLOW_WORKFLOW_DETAILS_FETCH_COMPLETED,
      ...workflowDetails,
    });
    fetchMissingStageRunSamples(Object.values(workflowDetails.stageRuns));
    StageEntryActions.refresh(affectedStageEntryIds);
  } finally {
    LoadingScreen.hide();
  }
}

interface StageRunsOutputSamplesFetchedAction {
  type: StageRunActionType.WORKFLOW_STAGE_RUNS_OUTPUT_SAMPLES_FETCHED;
  stageRuns: Array<StageRun>;
  entryIdToOrderedOutputSampleKeys: {[entryId: string]: Array<StageRunSampleKey>};
}

export function addOutputSamples(sampleKeys: Array<StageRunSampleKey>): void {
  LoadingScreen.show();
  StageRunDAO.addOutputSamples(sampleKeys)
    .done(
      ({
        stageRuns,
        entryIdToOrderedOutputSampleKeys,
      }: {
        stageRuns: Array<StageRun>;
        entryIdToOrderedOutputSampleKeys: {[entryId: string]: Array<StageRunSampleKey>};
      }) => {
        dispatchStageRunsFetchedAndFetchMissingSamples({stageRuns});
        getStore().dispatch({
          type: StageRunActionType.WORKFLOW_STAGE_RUNS_OUTPUT_SAMPLES_FETCHED,
          stageRuns,
          entryIdToOrderedOutputSampleKeys,
        });
      }
    )
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

export function bulkRemoveOutputSamples(
  samplesToRemove: Array<StageRunSampleKey>,
  {onSuccess}: {onSuccess: () => void}
): void {
  LoadingScreen.show();
  StageRunDAO.bulkRemoveOutputSamples(samplesToRemove)
    .done(
      ({
        stageRuns,
        entryIdToOrderedOutputSampleKeys,
      }: {
        stageRuns: Array<StageRun>;
        entryIdToOrderedOutputSampleKeys: {[entryId: string]: Array<StageRunSampleKey>};
      }) => {
        dispatchStageRunsFetchedAndFetchMissingSamples({stageRuns});
        getStore().dispatch({
          type: StageRunActionType.WORKFLOW_STAGE_RUNS_OUTPUT_SAMPLES_FETCHED,
          stageRuns,
          entryIdToOrderedOutputSampleKeys,
        });
        if (onSuccess) {
          onSuccess();
        }
      }
    )
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

export function rerunStageRun(stageRunId: string): void {
  LoadingScreen.show();
  StageRunDAO.rerunStageRun(stageRunId)
    .done(
      ({
        affectedStageRuns,
        affectedStageFlowNodes,
        affectedStageEntries,
        affectedStageConfigurations,
        entryIdToStructuredData,
      }: StageRunDAO.RerunStageRunResponse) => {
        fetchMissingStageRunSamples(affectedStageRuns);
        getStore().dispatch({
          type: StageRunActionType.WORKFLOW_GRAPH_CHANGED,
          affectedStageFlowNodes,
          affectedStageEntries,
          affectedStageConfigurations,
          affectedStageRuns,
          entryIdToStructuredData,
        });
        StageEntryActions.refresh(affectedStageEntries.map((stageEntry) => stageEntry.id));
      }
    )
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

function _dispatchInputSamplesChanged({
  stageRuns,
  stageEntryDetails,
  entryVersion,
}: {
  stageRuns: Array<StageRun>;
  stageEntryDetails: StageEntryDetails;
  entryVersion: ServerEntryVersion<FromServerId>;
}): void {
  dispatchStageRunsFetchedAndFetchMissingSamples({stageRuns});
  StageEntryActions.dispatchStageEntryDetailsChange(stageEntryDetails);
  StageEntryActions.dispatchStageEntryVersionChange(entryVersion, stageEntryDetails.stageEntry);
}

export async function replaceInputSample(
  stageRunId: string,
  args: StageRunDAO.ReplaceInputSampleParams
): Promise<void> {
  const {stageRun, stageEntryDetails, entryVersion} = await withLoadingScreen(
    StageRunDAO.replaceInputSample(stageRunId, args)
  );
  _dispatchInputSamplesChanged({
    stageRuns: [stageRun],
    stageEntryDetails,
    entryVersion,
  });
}

export async function bulkAddInputSamples(
  stageRunId: string,
  {
    samplesToAdd,
  }: {
    samplesToAdd: Array<{
      batchId: string | null;
      containerId: string | null;
      fileId: string | null;
    }>;
  }
): Promise<void> {
  const {stageRun, stageRunSamples, stageEntryDetails, entryVersion, storableSummaryExtras} =
    await StageRunDAO.bulkAddInputSamples(stageRunId, {samplesToAdd});
  dispatchStageRunSamplesFetched({stageRunSamples, storableSummaryExtras});
  _dispatchInputSamplesChanged({stageRuns: [stageRun], stageEntryDetails, entryVersion});
}

export function bulkRemoveInputSamples(
  stageEntryId: string,
  samplesToRemove: Array<StageRunSampleKey>,
  {onSuccess}: {onSuccess: () => void}
): void {
  LoadingScreen.show();
  StageRunDAO.bulkRemoveInputSamples(stageEntryId, samplesToRemove)
    .done(({stageRuns, stageEntryDetails, entryVersion}: StageRunDAO.BulkRemoveInputSamplesResponse) => {
      _dispatchInputSamplesChanged({stageRuns, stageEntryDetails, entryVersion});
      if (onSuccess) {
        onSuccess();
      }
    })
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

export async function autoselectInputSampleContainers(
  stageEntryId: string,
  selectedStageRunsAndSamples: Array<StageRunSampleKey>
): Promise<void> {
  const {numContainersAutoselected, stageRuns, stageEntryDetails, entryVersion} = await withLoadingScreen(
    StageRunDAO.autoselectInputSampleContainers(stageEntryId, selectedStageRunsAndSamples),
    'Finding available containers...'
  );
  if (numContainersAutoselected === 0) {
    notifications.warn('No available containers found.');
  } else if (numContainersAutoselected < selectedStageRunsAndSamples.length) {
    notifications.warn('Some samples had no available containers.');
  }
  _dispatchInputSamplesChanged({stageRuns, stageEntryDetails, entryVersion});
}

export type StageRunAction =
  | StageRunSamplesFetchStartedAction
  | WorkflowGraphChangedAction
  | StageEntriesFetchCompletedAction
  | StageRunsOutputSamplesFetchedAction;
